import { FC, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import qs from 'qs';
import { useTranslation } from 'react-i18next';

import BrokenImage from 'assets/image/broken-image.png';
import DoneImage from 'assets/image/done.svg';

import DialogStyled from 'shared/components/newUI/Dialog';
import ButtonCross from 'shared/components/Buttons/ButtonCross';
import ButtonDefault from 'shared/components/Buttons/ButtonsDefault';
import OpportunityBadges from 'shared/components/Table/Badges/OpportunityBadges';

import { AttachmentList } from 'shared/components/Attachments';
import MediaPreviewPopup from 'shared/components/MediaPreviewPopup';
import { PlayIcon } from 'shared/components/MediaPreviewPopup/MediaPreviewPopup.icons';

import { getDurationDetails, getOppoDate } from './helpers';
import { isVideoMedia, setUserPhotoSrc } from 'core/functions';
import { urls } from 'core/appUrls';

import { IChallengeResponse, IOpportunityResponse } from '@joc/api-gateway';

import styles from './PopupSoloOppoDetails.module.scss';

type OpportunityWithTypedSignature = IOpportunityResponse & {
	recipientSignatureName?: string | null;
};

type PopupSoloOppoDetailsProps = {
	open: boolean;
	onClose: () => void;
	opportunity: IOpportunityResponse;
	challenge?: IChallengeResponse;
};

const getTypedSignatureName = (opportunity: IOpportunityResponse): string | undefined => {
	const value = (opportunity as OpportunityWithTypedSignature).recipientSignatureName;
	if (typeof value !== 'string') return undefined;
	const trimmed = value.trim();
	return trimmed.length ? trimmed : undefined;
};

const PopupSoloOppoDetails: FC<PopupSoloOppoDetailsProps> = ({ open, onClose, opportunity, challenge }) => {
	const history = useHistory();
	const { t, i18n } = useTranslation();

	const [isMediaPreviewOpen, setIsMediaPreviewOpen] = useState(false);
	const [activeMediaIndex, setActiveMediaIndex] = useState(0);

	const handleOpenMedia = (index: number) => {
		setActiveMediaIndex(index);
		setIsMediaPreviewOpen(true);
	};

	const isAdminOppo = useMemo(
		() => !!(opportunity.volunteers?.length === 1 && opportunity.volunteers[0].userId !== opportunity.user?.id),
		[opportunity]
	);

	const oppoDate = useMemo(
		() => getOppoDate(opportunity.startDate, opportunity.endDate, opportunity.duration),
		[opportunity.startDate, opportunity.endDate, opportunity.duration]
	);
	const totalHours = useMemo(() => getDurationDetails(opportunity.duration, 'long'), [opportunity.duration]);

	const typedSignatureName = useMemo(() => getTypedSignatureName(opportunity), [opportunity]);
	const drawnSignatureImage = opportunity.recipientSignatureImage?.trim()
		? opportunity.recipientSignatureImage
		: undefined;
	const hasSignature = Boolean(drawnSignatureImage || typedSignatureName);
	const filePathes = opportunity?.filePathes || [];
	const attachments = opportunity?.attachments || [];

	const handleCreateChat = () => {
		const user = isAdminOppo ? opportunity?.volunteers?.[0] : opportunity.user;

		if (user && Object.keys(user).length) {
			const queryString = qs.stringify({ members: [(user as { chatId?: string }).chatId], type: 'all' });

			history.push(`${urls.inbox}?${queryString}`);
		}
	};

	return (
		<DialogStyled open={open} maxWidth="md" isFullScreen={true}>
			<section className={styles.opportunityDetails}>
				<div className={styles.opportunityDetails__header}>
					<ButtonCross clickHandler={onClose} parentClassName={styles.opportunityDetails__header_button} />

					<div className={styles.opportunityDetails__header__details}>
						{opportunity.isVerificated ? <OpportunityBadges language={i18n.language} /> : null}
						<h4 className={styles.opportunityDetails__header__details_name}>
							{opportunity.opportunityName}
						</h4>
						<div className={styles.opportunityDetails__header__details_divider} />
						<h4 className={styles.opportunityDetails__header__details_creator}>
							{`${opportunity.user?.firstName} ${opportunity.user?.lastName}`}
						</h4>
					</div>

					<div className={styles.opportunityDetails_content}>
						<div className={styles.opportunityDetails_mediaContainer}>
							{filePathes.length ? (
								filePathes.map((photo, index) => (
									<div
										key={`${photo}-${index}`}
										className={styles.opportunityDetails_image_container}
										onClick={() => handleOpenMedia(index)}
										role="button"
										tabIndex={0}
										onKeyDown={e => {
											if (e.key === 'Enter' || e.key === ' ') {
												e.preventDefault();
												handleOpenMedia(index);
											}
										}}
									>
										{isVideoMedia(photo) ? (
											<>
												<video
													src={`${process.env.REACT_APP_S3_API}/${photo}#t=0.001`}
													className={styles.opportunityDetails_image}
													preload="metadata"
													muted
													playsInline
												/>
												<div className={styles.opportunityDetails_playBadge}>
													<PlayIcon />
												</div>
											</>
										) : (
											<img
												alt="uploaded file"
												className={styles.opportunityDetails_image}
												src={`${process.env.REACT_APP_S3_API}/${photo}`}
											/>
										)}
									</div>
								))
							) : (
								<div className={styles.opportunityDetails_image_container}>
									<img
										alt="broken"
										className={styles.opportunityDetails_broken_image}
										src={BrokenImage}
									/>
								</div>
							)}
						</div>

						{opportunity.tagId && opportunity.tag && (
							<p
								className={styles.opportunityDetails_tag}
								title={opportunity.tag}
							>{`#${opportunity.tag}`}</p>
						)}

						<div className={styles.opportunityDetails__description}>
							<span className={styles.opportunityDetails__description_title}>Description:</span>
							<span
								className={styles.opportunityDetails__description_text}
								// eslint-disable-next-line react/no-danger
								dangerouslySetInnerHTML={{ __html: opportunity.opportunityDescription.trim() }}
							/>
						</div>

						{challenge ? (
							<div className={styles.opportunityDetails_challenge}>
								<p className={styles.opportunityDetails__mark_challenge}>Challenge</p>
								<span>
									Part of{' '}
									<b
										style={{ cursor: 'pointer' }}
										onClick={() => history.push(`${urls.marketing}?challenges`)}
									>
										{challenge.name}
									</b>
								</span>
							</div>
						) : null}

						{attachments.length > 0 && (
							<div className={styles.opportunityDetails_attachments}>
								<AttachmentList attachments={attachments} />
							</div>
						)}

						<div className={styles.opportunityDetails__info}>
							<div className={styles.opportunityDetails__info_item}>
								<span className={styles.opportunityDetails__info_item_label}>Date:</span>
								<span className={styles.opportunityDetails__info_item_text}>{oppoDate}</span>
							</div>
							<div className={styles.opportunityDetails__info_item}>
								<span className={styles.opportunityDetails__info_item_label}>Duration:</span>
								<span className={styles.opportunityDetails__info_item_text}>{totalHours}</span>
							</div>
							{opportunity.recipientName && (
								<div className={styles.opportunityDetails__info_item}>
									<span className={styles.opportunityDetails__info_item_label}>Recipient name:</span>
									<span className={styles.opportunityDetails__info_item_text}>
										{opportunity.recipientName}
									</span>
								</div>
							)}
							{opportunity.recipientEmail && (
								<div className={styles.opportunityDetails__info_item}>
									<span className={styles.opportunityDetails__info_item_label}>Recipient email:</span>
									<span className={styles.opportunityDetails__info_item_text}>
										{opportunity.recipientEmail}
									</span>
								</div>
							)}
							{opportunity.recipientPhoneNumber && (
								<div className={styles.opportunityDetails__info_item}>
									<span className={styles.opportunityDetails__info_item_label}>
										Recipient phone number:
									</span>
									<span className={styles.opportunityDetails__info_item_text}>
										{opportunity.recipientPhoneNumber}
									</span>
								</div>
							)}
						</div>

						<div className={styles.opportunityDetails__bottom}>
							{hasSignature ? (
								<div className={styles.opportunityDetails__bottom__signature}>
									{drawnSignatureImage ? (
										<img
											alt={t('opportunity.altSignature', { defaultValue: 'signature' })}
											className={styles.opportunityDetails__bottom__signature_image}
											src={setUserPhotoSrc(drawnSignatureImage)}
										/>
									) : (
										<div className={styles.opportunityDetails__bottom__signature_typed}>
											<span className={styles.opportunityDetails__bottom__signature_typedLabel}>
												{t('opportunity.typedElectronicSignature', {
													defaultValue: 'Typed electronic signature',
												})}
											</span>
											<span className={styles.opportunityDetails__bottom__signature_typedName}>
												{typedSignatureName}
											</span>
										</div>
									)}
									<div className={styles.opportunityDetails__bottom__signature_description}>
										<img
											src={DoneImage}
											alt={t('opportunity.verified', { defaultValue: 'verified' })}
										/>
										<span>{t('opportunity.verified', { defaultValue: 'verified' })}</span>
									</div>
								</div>
							) : null}
							<div className={styles.opportunityDetails__bottom__actions}>
								<ButtonDefault
									parentClassName={styles.full__width}
									title="Contact Volunteer"
									classList={['primary', 'lg']}
									clickHandler={handleCreateChat}
								/>
							</div>
						</div>
					</div>
				</div>
			</section>
			{filePathes.length > 0 && (
				<MediaPreviewPopup
					open={isMediaPreviewOpen}
					onClose={() => setIsMediaPreviewOpen(false)}
					items={filePathes}
					initialIndex={activeMediaIndex}
				/>
			)}
		</DialogStyled>
	);
};

export default PopupSoloOppoDetails;
